import React from 'react'
import styled from 'styled-components'
import { Text } from '@lmc/ui-kit/src/general'
import { useTranslation } from 'react-i18next'
import { NAMESPACE } from 'locale'
import { FormCard } from '@lmc/ui-kit'
import dayjs from 'dayjs'

type StockCheckDetailContentProps = {
  record: any
  medicationName: string
}

export const StockCheckDetailContent: React.FC<
  StockCheckDetailContentProps
> = ({ record }) => {
  const { t } = useTranslation(NAMESPACE.EMAR_V2)

  const description = record?.description || record?.title || ''
  const stockCount = record?.amount1
  const expectedStock = record?.amount2

  const discrepancy =
    stockCount !== undefined && expectedStock !== undefined
      ? stockCount - expectedStock
      : null

  // For stock check, received by/date/from might not be available, so we'll use loggedBy data or leave empty
  const loggedBy = record?.loggedBy
  const receivedBy = record?.receivedBy || loggedBy
  const receivedByName = receivedBy
    ? `${receivedBy.name?.first || ''} ${receivedBy.name?.last || ''}`.trim()
    : ''
  const receivedDate = record?.receivedDate
    ? dayjs(record.receivedDate).format('DD-MM-YYYY')
    : record?.timeLogged
      ? dayjs(record.timeLogged).format('DD-MM-YYYY')
      : ''
  const receivedFrom = record?.receivedFrom || ''

  return (
    <FormCard level={2}>
      <InfoRow>
        <FullWidthSection>
          <StyledLabel level={2}>{t('DESCRIPTION')}</StyledLabel>
          <Text>{description || '-'}</Text>
        </FullWidthSection>
      </InfoRow>
      <InfoRow>
        <InfoSection>
          <StyledLabel level={2}>{t('EXPECTED_STOCK')}</StyledLabel>
          <Text weight="bold">
            {expectedStock !== undefined
              ? `${expectedStock} ${t('TABLETS')}`
              : '-'}
          </Text>
        </InfoSection>
        <InfoSection>
          <StyledLabel level={2}>{t('STOCK_COUNT')}</StyledLabel>
          <Text weight="bold">
            {stockCount !== undefined ? `${stockCount} ${t('TABLETS')}` : '-'}
          </Text>
        </InfoSection>
        <InfoSection>
          <StyledLabel level={2}>{t('DISCREPANCY')}</StyledLabel>
          {discrepancy !== null && discrepancy !== 0 ? (
            <DiscrepancyValue weight="bold">
              {discrepancy > 0 ? '+' : ''}
              {discrepancy} {t('TABLETS')}
            </DiscrepancyValue>
          ) : (
            <Text weight="bold">
              {discrepancy === 0 ? `0 ${t('TABLETS')}` : '-'}
            </Text>
          )}
        </InfoSection>
      </InfoRow>
      {(receivedByName || receivedDate || receivedFrom) && (
        <InfoRow>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVED_BY')}</StyledLabel>
            <Text weight="bold">{receivedByName || '-'}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVE_DATE')}</StyledLabel>
            <Text weight="bold">{receivedDate || '-'}</Text>
          </InfoSection>
          <InfoSection>
            <StyledLabel level={2}>{t('RECEIVED_FROM')}</StyledLabel>
            <Text weight="bold">{receivedFrom || '-'}</Text>
          </InfoSection>
        </InfoRow>
      )}
    </FormCard>
  )
}

const InfoRow = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  gap: 24px;
`

const InfoSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 185px;
`

const FullWidthSection = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
`

const StyledLabel = styled(Text)`
  color: ${({ theme }) => theme.color.neutral.color5};
`

const DiscrepancyValue = styled(Text)<{
  $isPositive?: boolean
  $noDiscrepancy?: boolean
}>`
  color: ${({ theme }) => theme.color.status.warning};
`
